/**
 * Time arithmetic. Kept out of the views so the Time page and the client report
 * can never disagree about what a week is or how an amount is computed.
 */

import { kindIn, kindOf, type EntryKind } from "./billing.ts";
import type { Client, Project, TimeEntry } from "./types";

export type Period = "day" | "week" | "month" | "quarter" | "all";

/**
 * A calendar day in LOCAL time, as YYYY-MM-DD.
 *
 * Ported from the time-tracker, and load-bearing: `new Date().toISOString()`
 * is UTC, so slicing its first ten characters puts anything logged after ~6pm
 * US Central onto the following day. The day headings render with
 * toLocaleDateString (local), so a UTC key and a local label disagree, and an
 * evening's work splits across two headings.
 */
export function toIsoLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The local calendar day an entry belongs to. Use this as a grouping key. */
export const entryDayKey = (entry: TimeEntry): string => toIsoLocal(new Date(entry.start));

export interface PeriodBounds {
  start: Date;
  end: Date;
  label: string;
  isCurrent: boolean;
}

/**
 * One period calculation for every surface that reports on a range, so the
 * Time view and the client report can never drift on what "last month" means.
 * `offset` steps whole periods back: -1 is the previous week/month/quarter.
 *
 * Ported from the time-tracker, where the same drift between two Insights
 * views is what prompted it.
 */
export function periodBounds(period: Period, offset = 0, now = new Date()): PeriodBounds {
  const off = Number.isFinite(offset) ? Math.round(offset) : 0;

  if (period === "all") {
    return { start: new Date(0), end: new Date(8.64e15), label: "All time", isCurrent: true };
  }

  if (period === "day") {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() + off);
    const end = new Date(start);
    end.setHours(23, 59, 59, 999);
    return {
      start, end,
      label: start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
      isCurrent: off === 0,
    };
  }

  if (period === "week") {
    // Monday-based: a consulting week is Mon–Sun, and a Sunday-based week
    // splits the working week across two buckets.
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7) + off * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    const short = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return { start, end, label: `${short(start)} – ${short(end)}`, isCurrent: off === 0 };
  }

  if (period === "month") {
    const start = new Date(now.getFullYear(), now.getMonth() + off, 1);
    // Day 0 of the next month is the last day of this one, leap years included.
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59, 999);
    return {
      start, end,
      label: start.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
      isCurrent: off === 0,
    };
  }

  const startMonth = Math.floor(now.getMonth() / 3) * 3 + off * 3;
  const start = new Date(now.getFullYear(), startMonth, 1);
  const end = new Date(start.getFullYear(), start.getMonth() + 3, 0, 23, 59, 59, 999);
  return {
    start, end,
    label: `Q${Math.floor(start.getMonth() / 3) + 1} ${start.getFullYear()}`,
    isCurrent: off === 0,
  };
}

export function inBounds(entry: TimeEntry, bounds: PeriodBounds): boolean {
  const t = new Date(entry.start).getTime();
  return t >= bounds.start.getTime() && t <= bounds.end.getTime();
}

/**
 * What an entry is called when its note is blank. One rule, so no two surfaces
 * invent a different placeholder — the time-tracker had five, most of them
 * "Untitled entry", which tells you nothing when you come back to invoice it.
 * Most specific thing the user actually chose wins.
 */
export const GENERAL_WORK_LABEL = "General project work";

export function deriveEntryLabel(note?: string, taskTitle?: string): string {
  const typed = (note ?? "").trim();
  if (typed) return typed;
  const task = (taskTitle ?? "").trim();
  if (task) return task;
  return GENERAL_WORK_LABEL;
}

export interface Totals {
  seconds: number;
  /** Billable (hourly, charged) time — the only kind that carries money. */
  billableSeconds: number;
  /** Fixed-fee / retainer work: already paid for, counts against the budget. */
  projectSeconds: number;
  /** Client work deliberately not billed. */
  noChargeSeconds: number;
  /** Admin and overhead. */
  internalSeconds: number;
  /** Everything done for a client: billable + project work + no charge. */
  clientSeconds: number;
  /** Σ hours × rateSnapshot, billable time only. */
  amount: number;
  /** Billable and not yet on an invoice — what is still owed a bill. */
  unbilledSeconds: number;
  unbilledAmount: number;
  currency: string;
}

/**
 * Billable work that has not gone out on an invoice yet. Needs the projects:
 * time on a fixed-fee or retainer project is never owed a bill, however its
 * switch was set (see lib/billing).
 */
export const isUnbilled = (e: TimeEntry, projects: Pick<Project, "id" | "billingType">[]): boolean =>
  kindIn(e, projects) === "hourly" && !e.invoicedAt;

/** The kind of every entry, by the rules in lib/billing. */
export const entryKind = (e: TimeEntry, projects: Pick<Project, "id" | "billingType">[]): EntryKind => kindIn(e, projects);

export function totalsOf(entries: TimeEntry[], projects: Pick<Project, "id" | "billingType">[]): Totals {
  let seconds = 0, billableSeconds = 0, projectSeconds = 0, noChargeSeconds = 0, internalSeconds = 0;
  let amount = 0, unbilledSeconds = 0, unbilledAmount = 0;
  let currency = "USD";
  for (const e of entries) {
    const s = e.durationSeconds || 0;
    seconds += s;
    const kind = kindIn(e, projects);
    if (kind === "project") projectSeconds += s;
    else if (kind === "no_charge") noChargeSeconds += s;
    else if (kind === "internal") internalSeconds += s;
    else {
      const value = (s / 3600) * (e.rateSnapshot ?? 0);
      billableSeconds += s;
      amount += value;
      if (!e.invoicedAt) {
        unbilledSeconds += s;
        unbilledAmount += value;
      }
      if (e.currency) currency = e.currency;
    }
  }
  return {
    seconds, billableSeconds, projectSeconds, noChargeSeconds, internalSeconds,
    clientSeconds: billableSeconds + projectSeconds + noChargeSeconds,
    amount, unbilledSeconds, unbilledAmount, currency,
  };
}

/** The kinds present in a set of totals, largest-meaning first, zeros left out. */
export function kindBreakdown(t: Totals): [EntryKind, number][] {
  const rows: [EntryKind, number][] = [
    ["hourly", t.billableSeconds], ["project", t.projectSeconds],
    ["no_charge", t.noChargeSeconds], ["internal", t.internalSeconds],
  ];
  return rows.filter(([, s]) => s > 0);
}

const KIND_WORD: Record<EntryKind, string> = {
  hourly: "billable", project: "project work", no_charge: "no charge", internal: "internal",
};

/** "2:30 billable · 1:00 project work · 0:45 internal", for summary lines. */
export function breakdownText(t: Totals, fmt: (seconds: number) => string): string {
  return kindBreakdown(t).map(([k, s]) => `${fmt(s)} ${KIND_WORD[k]}`).join(" · ");
}

/** Group entries by a key, preserving the order the keys first appear. */
export function groupBy<T>(rows: T[], key: (row: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    const bucket = out.get(k);
    if (bucket) bucket.push(row);
    else out.set(k, [row]);
  }
  return out;
}

/**
 * Hours to TWO decimals. One decimal rounds a quarter-hour to 0.3, which is
 * both wrong and the number you would put on an invoice — 0.25 is exact for
 * every 15-minute increment, which is how consulting time is actually billed.
 */
export const hours = (seconds: number): string => (seconds / 3600).toFixed(2);

/**
 * "5:15" — hours and minutes on a clock face.
 *
 * `hours()` gives 5.25, which is the right form in a column of figures you are
 * going to invoice. This is the right form for the headline on the Today
 * screen, where you are reading it as a length of time rather than a quantity.
 */
export function clock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds / 60));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * Snap a duration to a billing increment.
 *
 * The floor matters as much as the rounding: a four-minute call at a 15-minute
 * increment rounds to 15, not to zero. An entry that rounds away is an entry
 * you have to remember was ever there.
 *
 * Only the STORED duration snaps. The running timer still ticks in real time —
 * a clock that jumped in fifteen-minute steps would be unreadable.
 */
export function applyRounding(seconds: number, incrementMinutes: number): number {
  const exact = Math.max(0, Math.round(seconds));
  if (incrementMinutes <= 1) return exact;
  const inc = incrementMinutes * 60;
  return Math.max(inc, Math.round(exact / inc) * inc);
}

/** Duration as people say it: 45m, 2h, 1h30m. */
export function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h && m) return `${h}h${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/**
 * The fee as shown on a project: from the fee amount on a fixed-fee or
 * retainer project ("$62,000", "$4,000/mo"). Older projects may only have the
 * free-text `fee` they were given before the amount existed; that shows as-is.
 */
export function feeLabel(p: Pick<Project, "billingType" | "feeAmount" | "fee" | "currency">): string {
  const flat = p.billingType === "fixed" || p.billingType === "retainer";
  if (flat && p.feeAmount && p.feeAmount > 0) {
    return money(p.feeAmount, p.currency || "USD") + (p.billingType === "retainer" ? "/mo" : "");
  }
  return p.fee?.trim() ?? "";
}

export function money(amount: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency, maximumFractionDigits: 0,
  }).format(amount);
}

export const projectName = (projects: Project[], id?: string): string =>
  projects.find((p) => p.id === id)?.name ?? "Unknown project";

export const clientNameOf = (clients: Client[], id?: string): string | null =>
  clients.find((c) => c.id === id)?.name ?? null;

/** "Thu 4 Sep" — the day heading in the entry list. */
export function dayLabel(iso: string): string {
  // A bare "2026-10-06" parses as midnight UTC — the evening before, anywhere
  // west of Greenwich — so the label read a day early. Read it as local.
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const date = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(iso);
  return date.toLocaleDateString(undefined, {
    weekday: "short", day: "numeric", month: "short",
  });
}

export function clockRange(entry: TimeEntry): string {
  const t = (iso?: string) =>
    iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";
  return entry.end ? `${t(entry.start)} – ${t(entry.end)}` : `${t(entry.start)} – running`;
}


/* -- Budget ------------------------------------------------------------------ */

export interface BudgetStatus {
  /** "fixed" burns down once over the engagement; "retainer" resets monthly. */
  kind: "fixed" | "retainer";
  budgetHours: number;
  usedSeconds: number;
  /** >1 means over budget. */
  ratio: number;
  /** What window `usedSeconds` covers, for the caption. */
  window: string;
  /** What the time is really earning: the fee (per month, for a retainer)
   *  divided by the hours worked. Null with no fee amount or no hours yet. */
  effectiveRate: number | null;
}

/** Work on the project that counts against its budget: Internal time logged
 *  against it (the proposal that won it, say) is yours, not the client's. */
const budgetTime = (project: Project, e: TimeEntry) => e.project === project.id && kindOf(e, project) !== "internal";
const rateOf = (fee: number | undefined, seconds: number) => (fee && seconds > 0 ? fee / (seconds / 3600) : null);

/**
 * Hours used against hours sold.
 *
 * The two billing shapes are not interchangeable, which is why `billingType`
 * exists: fixed-fee counts every hour ever logged to the project, a retainer
 * counts only the hours inside the month being viewed. Summing a retainer over
 * a year would show a wildly over-budget project that is in fact perfectly on
 * track every month.
 */
export function budgetStatus(
  project: Project,
  entries: TimeEntry[],
  now = new Date(),
): BudgetStatus | null {
  const type = project.billingType;

  if (type === "fixed" && project.budgetHours) {
    const used = entries
      .filter((e) => budgetTime(project, e))
      .reduce((n, e) => n + (e.durationSeconds || 0), 0);
    return {
      kind: "fixed",
      budgetHours: project.budgetHours,
      usedSeconds: used,
      ratio: used / 3600 / project.budgetHours,
      window: "whole engagement",
      effectiveRate: rateOf(project.feeAmount, used),
    };
  }

  if (type === "retainer" && project.retainerHours) {
    const month = periodBounds("month", 0, now);
    const used = entries
      .filter((e) => budgetTime(project, e) && inBounds(e, month))
      .reduce((n, e) => n + (e.durationSeconds || 0), 0);
    return {
      kind: "retainer",
      budgetHours: project.retainerHours,
      usedSeconds: used,
      ratio: used / 3600 / project.retainerHours,
      window: month.label,
      effectiveRate: rateOf(project.feeAmount, used),
    };
  }

  return null;
}


/**
 * Parse a typed duration into minutes.
 *
 * Accepts what people actually type for a length of time: "1:30" and "0:45"
 * (hours:minutes), "90" (bare minutes), "1.5h", "90m", "1h30". Returns null for
 * anything it cannot read, so a caller can refuse rather than silently log a
 * wrong number — the failure mode that matters here is a plausible-but-wrong
 * duration, not a rejected one.
 */
export function parseDuration(input: string): number | null {
  const raw = input.trim().toLowerCase();
  if (!raw) return null;

  // H:MM — the format asked for, and the only one where ":" is meaningful.
  const clock = /^(\d{1,3}):([0-5]?\d)$/.exec(raw);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);

  // "1h30", "1h 30m", "2h"
  const hm = /^(\d{1,3})\s*h\s*(\d{1,2})?\s*m?$/.exec(raw);
  if (hm) return Number(hm[1]) * 60 + Number(hm[2] ?? 0);

  // "45m"
  const mins = /^(\d{1,4})\s*m$/.exec(raw);
  if (mins) return Number(mins[1]);

  // "1.5h" / "1.5"  — decimal hours
  const dec = /^(\d+(?:\.\d+)?)\s*h$/.exec(raw);
  if (dec) return Math.round(Number(dec[1]) * 60);

  // Bare number is minutes, matching the quick buttons beside it.
  const bare = /^(\d{1,4})$/.exec(raw);
  if (bare) return Number(bare[1]);

  return null;
}


export interface RetainerMonth {
  label: string;
  /** Hours logged in that month. */
  hours: number;
  ratio: number;
  /** The engagement had not started — no data and no hours are different facts. */
  beforeStart: boolean;
}

/**
 * Twelve months of retainer usage, oldest first.
 *
 * `budgetStatus` scopes a retainer to the current month, which is right for
 * "am I over now" but hides that a client ran 12 h over in July and 9 h under
 * in August. This maps the same `periodBounds` over the last twelve months, so
 * the strip and the bar cannot disagree about what a month is.
 */
export function retainerHistory(
  project: Project,
  entries: TimeEntry[],
  now = new Date(),
): RetainerMonth[] {
  const allowance = project.retainerHours ?? 0;
  if (project.billingType !== "retainer" || allowance <= 0) return [];

  const start = project.start ? new Date(project.start) : null;

  return Array.from({ length: 12 }, (_, i) => {
    const bounds = periodBounds("month", i - 11, now);
    const seconds = entries
      .filter((e) => budgetTime(project, e) && inBounds(e, bounds))
      .reduce((n, e) => n + (e.durationSeconds || 0), 0);
    return {
      label: bounds.start.toLocaleDateString(undefined, { month: "short" }),
      hours: seconds / 3600,
      ratio: seconds / 3600 / allowance,
      beforeStart: Boolean(start && bounds.end < start),
    };
  });
}

/** "Four over, one exactly at it" — a finding, not a legend. */
export function retainerFinding(months: RetainerMonth[]): string {
  const live = months.filter((m) => !m.beforeStart);
  if (live.length === 0) return "No months on record yet.";
  const over = live.filter((m) => m.ratio > 1).length;
  const exact = live.filter((m) => m.ratio === 1).length;
  const under = live.filter((m) => m.ratio < 1 && m.hours > 0).length;
  const parts = [
    over ? `${over} over` : null,
    exact ? `${exact} exactly at it` : null,
    under ? `${under} under` : null,
  ].filter(Boolean);
  return parts.length ? `${parts.join(", ")}.` : "Nothing logged in the last twelve months.";
}
